/**
 * A QR code for a URL, so a phone doesn't have to be typed at. Generic SVG rendering,
 * currently used only by `InvitesCard.tsx` for an invite's registration link — this used
 * to be shared with a `TokenQr` wrapper for a minted token's own sign-in link too, removed
 * along with manual token minting (D201). Kept as its own component rather than folded
 * into `InvitesCard.tsx` in case a second caller (pairing's own code, say) ever wants it.
 */

import qrcode from "qrcode-generator";

export function QrCode({
  url,
  size = 168,
  label,
  caption,
}: {
  url: string;
  size?: number;
  label: string;
  caption: string;
}) {
  // Type 0 lets the library pick the smallest version that fits; "M" tolerates ~15% damage,
  // which is ample for a screen being photographed head-on.
  const qr = qrcode(0, "M");
  qr.addData(url);
  qr.make();

  const count = qr.getModuleCount();
  const cells: string[] = [];
  for (let row = 0; row < count; row++) {
    for (let col = 0; col < count; col++) {
      if (qr.isDark(row, col)) cells.push(`M${col} ${row}h1v1h-1z`);
    }
  }

  return (
    <div className="token-qr">
      <svg
        width={size}
        height={size}
        viewBox={`-1 -1 ${count + 2} ${count + 2}`}
        role="img"
        aria-label={label}
        shapeRendering="crispEdges"
      >
        {/* Quiet zone included in the viewBox; light background so cameras cope. */}
        <rect x={-1} y={-1} width={count + 2} height={count + 2} fill="#ffffff" />
        <path d={cells.join("")} fill="#000000" />
      </svg>
      <p className="small muted" style={{ margin: "0.4rem 0 0", maxWidth: `${size}px` }}>
        {caption}
      </p>
    </div>
  );
}
